import { getProductVariants } from './productVariants';
import { sameValue, VARIANT_FIELDS } from './productModel';

const map = value => value && value.constructor === Object;
const mergeFields = (base, current, local) => {
  if (!map(local)) return local;
  base = map(base) ? base : {};
  const next = { ...(map(current) ? current : {}) };
  for (const key of new Set([...Object.keys(base), ...Object.keys(local)])) {
    if (sameValue(base[key], local[key])) continue;
    if (!(key in local)) delete next[key];
    else next[key] = local[key];
  }
  return next;
};

// Rebase the user's dirty values onto the current record. No remote-change
// review is required: the last save wins only for values the user changed.
export const rebaseProductPatch = (base = {}, current = {}, supplied = {}) => {
  const patch = Object.fromEntries(Object.entries(supplied).filter(([key, value]) => !sameValue(base[key], value)));
  if (VARIANT_FIELDS.some(key => key in patch)) {
    const before = getProductVariants(base);
    const localProduct = { ...base, ...patch, styleIds: patch.styleIds || before.map(v => v.id) };
    const local = getProductVariants(localProduct);
    const remote = getProductVariants(current.styleIds ? current : { ...current, styleIds: before.map(v => v.id) });
    const beforeIds = new Set(before.map(v => v.id)), localIds = new Set(local.map(v => v.id));
    const changed = v => { const b = before.find(x => x.id === v.id); return !b || VARIANT_FIELDS.some(k => k !== 'styleIds' && k in patch && !sameValue(base[k]?.[b.index], localProduct[k]?.[v.index])); };
    const ids = [...remote.map(v => v.id).filter(id => !beforeIds.has(id) || localIds.has(id)), ...local.filter(changed).map(v => v.id).filter(id => !remote.some(v => v.id === id))];
    const rows = ids.map(id => {
      const b = before.find(v => v.id === id), r = remote.find(v => v.id === id), l = local.find(v => v.id === id);
      const values = {};
      for (const key of VARIANT_FIELDS.filter(k => k !== 'styleIds')) {
        const bv = base[key]?.[b?.index], rv = current[key]?.[r?.index], lv = localProduct[key]?.[l?.index];
        values[key] = l && (!b || (key in patch && !sameValue(bv, lv)))
          ? key === 'styleDetails' ? mergeFields(bv, rv, lv) : lv
          : rv;
      }
      return values;
    });
    for (const key of VARIANT_FIELDS) {
      // Keep positional arrays aligned, including concurrently added variants.
      const value = key === 'styleIds' ? ids : rows.map(r => r[key] ?? (key === 'styleDetails' ? null : ''));
      if (!sameValue(current[key], value)) patch[key] = value;
      else delete patch[key];
    }
  }
  if (Array.isArray(patch.photos) && Array.isArray(base.photos)) {
    const keyOf = p => typeof p === 'string' ? p : p?.id || p?.url;
    const local = patch.photos, before = base.photos, remote = Array.isArray(current.photos) ? current.photos : [];
    const localKeys = new Set(local.map(keyOf)), beforeKeys = new Set(before.map(keyOf));
    const keys = [...remote.map(keyOf).filter(k => !beforeKeys.has(k) || localKeys.has(k)), ...local.map(keyOf).filter(k => !remote.some(p => keyOf(p) === k))];
    patch.photos = keys.map(key => {
      const b = before.find(p => keyOf(p) === key), r = remote.find(p => keyOf(p) === key), l = local.find(p => keyOf(p) === key);
      return l === undefined ? r : b === undefined ? l : mergeFields(b, r, l);
    });
  }
  return Object.fromEntries(Object.entries(patch).filter(([key, value]) => !sameValue(current[key], value)));
};
